import React, { useState, useEffect } from 'react';
import { isDayCompleted, getLastUnlockedDay, updateLastUnlockedDay, getDayStars, getTotalStars } from '../utils/completionStorage';
import StarRating from './StarRating';
import LockedDayPopup from './LockedDayPopup';
import StarIcon from './StarIcon';
import { sound } from '../utils/sound';
import { debugLog, debugError } from '../utils/debug';

const DEFAULT_OFF_DAY_SYMBOL = '❌';

interface CalendarGridProps {
  startDay: number;
  startColumn: number;
  year: number;
  month: number;
  daysPerRow: number;
  availableDays: number[];
  specialDays: {
    [key: number]: {
      type: string;
      count?: number;
      reason?: string;
    };
  };
  onDayClick: (day: number, isUnlocked: boolean, isCurrentDay: boolean) => void;
  serverDate: Date | null;
}

interface DayStatus {
  day: number;
  unlocked: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';

const CalendarGrid: React.FC<CalendarGridProps> = ({ startDay, startColumn, year, month, daysPerRow, availableDays, specialDays, onDayClick, serverDate }) => {
  const [dayStatuses, setDayStatuses] = useState<Map<number, boolean>>(new Map());
  const [currentDayIndex, setCurrentDayIndex] = useState<number | null>(null);
  const [newlyUnlockedDays, setNewlyUnlockedDays] = useState<Set<number>>(new Set());
  const previousStatusesRef = React.useRef<Map<number, boolean>>(new Map());
  const [lockedDayMessage, setLockedDayMessage] = useState<string | null>(null);
  const [lockedDayNumber, setLockedDayNumber] = useState<number | null>(null);
  const [currentDate, setCurrentDate] = useState<Date | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render when stars change
  const animationClearTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingAnimationsRef = React.useRef<Set<number>>(new Set());
  const [statusesLoaded, setStatusesLoaded] = useState(false); // Track if initial statuses are loaded

  // Helper function to apply pending unlock animations
  const applyPendingAnimations = () => {
    debugLog(1, '[CalendarGrid] Applying pending animations, pending count:', pendingAnimationsRef.current.size);
    if (pendingAnimationsRef.current.size > 0) {
      // Determine which types of days were unlocked
      let hasSpecialDays = false;
      let hasRegularDays = false;

      pendingAnimationsRef.current.forEach(dayIndex => {
        const specialDay = specialDays[dayIndex];
        const dayType = specialDay?.type || 'normal';

        if (dayType === 'requireRow' || dayType === 'requireStars') {
          hasSpecialDays = true;
        } else {
          hasRegularDays = true;
        }
      });

      // Play appropriate sounds (only one instance, prefer special if both)
      if (hasSpecialDays) {
        sound.unlockSpecial();
      }
      else if (hasRegularDays) {
        sound.unlockRegular();
      }

      setNewlyUnlockedDays(new Set(pendingAnimationsRef.current));
      pendingAnimationsRef.current = new Set();

      // Start clear timer after 2 seconds
      animationClearTimerRef.current = setTimeout(() => {
        setNewlyUnlockedDays(new Set());
        animationClearTimerRef.current = null;
      }, 2000);
    }
  };

  // Check unlock statuses when serverDate becomes available
  useEffect(() => {
    if (serverDate && !statusesLoaded) {
      debugLog(1, '[CalendarGrid] serverDate available, checking initial unlock statuses');
      checkUnlockStatuses();
    }
  }, [serverDate, statusesLoaded]);

  // Listen for events that should trigger unlock checks with animations
  useEffect(() => {
    const handleScoringComplete = () => {
      debugLog(1, '[CalendarGrid] scoringComplete event received - checking unlocks');
      checkUnlockStatuses();
    };

    const handleInfoPopupClosed = () => {
      debugLog(1, '[CalendarGrid] infoPopupClosed event received - checking unlocks');
      checkUnlockStatuses();
    };

    const handleInitialLoad = () => {
      debugLog(1, '[CalendarGrid] initialLoad event received - checking unlocks');
      checkUnlockStatuses();
    };

    const handleSyncCompleted = () => {
      debugLog(1, '[CalendarGrid] syncCompleted event received - checking unlocks without animations');
      // Sync completed - check unlock statuses without animations
      checkUnlockStatuses(true); // Pass true to indicate this is from sync
    };

    window.addEventListener('scoringComplete', handleScoringComplete);
    window.addEventListener('infoPopupClosed', handleInfoPopupClosed);
    window.addEventListener('initialLoad', handleInitialLoad);
    window.addEventListener('syncCompleted', handleSyncCompleted);

    return () => {
      window.removeEventListener('scoringComplete', handleScoringComplete);
      window.removeEventListener('infoPopupClosed', handleInfoPopupClosed);
      window.removeEventListener('initialLoad', handleInitialLoad);
      window.removeEventListener('syncCompleted', handleSyncCompleted);
    };
  }, []);

  const checkUnlockStatuses = async (isFromSync: boolean = false) => {
    try {
      // If serverDate is not available yet, skip
      if (!serverDate) {
        return;
      }

      // Store previous statuses to detect newly unlocked requireRow days
      // Use ref to persist across renders
      const previousStatuses = previousStatusesRef.current;

      setCurrentDate(serverDate);

      // Calculate unlock status for available days based on server date
      // Map from day index (1, 2, 3...) to unlock status
      const statuses = new Map<number, boolean>();
      let todayDayIndex: number | null = null;

      // Only process available days
      availableDays.forEach((dayIndex) => {
        // Calculate target date based on day number
        // Day numbers encode month: 1-99 = first month, 100-199 = second month, etc.
        const monthOffset = Math.floor(dayIndex / 100);
        const dayOfMonth = dayIndex % 100;
        const targetDate = new Date(year, (month - 1) + monthOffset, dayOfMonth);
        targetDate.setUTCHours(12, 0, 0, 0); // Normalize time to mid-day GMT

        // Check if date has arrived
        const dateUnlocked = serverDate >= targetDate;

        // Check if this day is requireRow or requireStars type
        const specialDay = specialDays[dayIndex];
        const dayType = specialDay?.type || 'normal';
        let isUnlocked = dateUnlocked;

        // If the day is already completed (has stars), always unlock it
        // This handles synced data from other devices
        const dayStars = getDayStars(dayIndex);
        if (dayStars > 0) {
          isUnlocked = true;
        } else if (dayType === 'requireRow' && dateUnlocked) {
          // For requireRow days, also check if previous daysPerRow-1 days are complete
          const previousDays = [];
          for (let i = 1; i < daysPerRow; i++) {
            const prevDayIndex = dayIndex - i;
            if (availableDays.includes(prevDayIndex)) {
              previousDays.push(prevDayIndex);
            }
          }

          // Check if all previous days in the row are completed
          const allPreviousComplete = previousDays.every(prevDay => isDayCompleted(prevDay));
          isUnlocked = dateUnlocked && allPreviousComplete;
        } else if (dayType === 'requireStars' && dateUnlocked) {
          // For requireStars days, check if total stars meets requirement
          const requiredStars = specialDay?.count;
          if (requiredStars !== undefined) {
            const currentTotalStars = getTotalStars();
            isUnlocked = dateUnlocked && currentTotalStars >= requiredStars;
          }
        }

        statuses.set(dayIndex, isUnlocked);

        // Check if this is today
        if (
          serverDate.getFullYear() === targetDate.getFullYear() &&
          serverDate.getMonth() === targetDate.getMonth() &&
          serverDate.getDate() === targetDate.getDate()
        ) {
          todayDayIndex = dayIndex;
        }
      });

      setDayStatuses(statuses);
      setCurrentDayIndex(todayDayIndex);
      setStatusesLoaded(true); // Mark statuses as loaded

      // Check if we've reached the last day and dispatch event
      const lastDay = Math.max(...availableDays);
      if (todayDayIndex !== null && todayDayIndex >= lastDay) {
        window.dispatchEvent(new CustomEvent('lastDayArrived'));
      }

      // If this is from sync, don't process animations or update previousStatusesRef
      if (isFromSync) {
        debugLog(1, '[CalendarGrid] Sync check complete - skipping animation processing and not updating previousStatusesRef');
        return;
      }

      debugLog(1, '[CalendarGrid] Non-sync check - will process animations if any newly unlocked days found');

      // Check for newly unlocked requireRow days - use a slight delay to ensure DOM is ready
      setTimeout(() => {
        const lastUnlockedDay = getLastUnlockedDay();
        const newlyUnlocked = new Set<number>();
        const isFirstLoad = previousStatuses.size === 0;

        debugLog(1, '[CalendarGrid] Checking for newly unlocked days - isFirstLoad:', isFirstLoad, 'lastUnlockedDay:', lastUnlockedDay, 'previousStatuses size:', previousStatuses.size);

        // Only check available days
        for (const dayIndex of availableDays) {
          const isUnlocked = statuses.get(dayIndex) || false;
          const wasUnlocked = previousStatuses.get(dayIndex);
          const specialDay = specialDays[dayIndex];
          const dayType = specialDay?.type || 'normal';

          // Only animate if the unlock status actually changed
          let justUnlocked = false;

          if (isFirstLoad) {
            // On first load, animate normal days that are newly unlocked based on lastUnlockedDay
            // For requireRow and requireStars days, check if they were previously locked
            if (dayType === 'requireRow' || dayType === 'requireStars') {
              // If it's unlocked now but wasn't tracked as unlocked before, it just unlocked
              justUnlocked = isUnlocked && dayIndex > lastUnlockedDay;
            } else {
              justUnlocked = isUnlocked && dayIndex > lastUnlockedDay;
            }
          } else {
            // On subsequent checks, only animate if status changed from false to true
            justUnlocked = isUnlocked && wasUnlocked === false;
          }

          // Check if this is a newly unlocked day
          // For requireRow and requireStars days, only animate if it just became unlocked
          if ((dayType === 'requireRow' || dayType === 'requireStars') && justUnlocked) {
            newlyUnlocked.add(dayIndex);
            updateLastUnlockedDay(dayIndex);
          }
          // For normal days, check against last unlocked day (only on first load)
          else if (dayType !== 'requireRow' && dayType !== 'requireStars' && justUnlocked && isFirstLoad) {
            newlyUnlocked.add(dayIndex);
            updateLastUnlockedDay(dayIndex);
          }
        }

        if (newlyUnlocked.size > 0) {
          debugLog(1, '[CalendarGrid] Found newly unlocked days:', newlyUnlocked);
          // Store in ref
          pendingAnimationsRef.current = newlyUnlocked;

          // Apply animations immediately
          debugLog(1, '[CalendarGrid] Applying animations for newly unlocked days');
          // Use a small delay to ensure DOM is ready
          setTimeout(() => {
            applyPendingAnimations();
          }, 100);
        }

        // Update the ref for next comparison (after processing animations)
        previousStatusesRef.current = new Map(statuses);
      }, 100);
    } catch (err) {
      debugError('Error fetching current date:', err);
      // Fallback to all locked if we can't reach the server
      const statuses = new Map<number, boolean>();
      for (const dayIndex of availableDays) {
        statuses.set(dayIndex, false);
      }
      setDayStatuses(statuses);
      setStatusesLoaded(true); // Mark as loaded even on error to prevent infinite loading
    }
  };

  const handleClick = (dayIndex: number) => {
    const specialDay = specialDays[dayIndex];
    const dayType = specialDay?.type || 'normal';

    // Handle "off" type days - always disabled
    if (dayType === 'off') {
      setLockedDayMessage('No task assigned!');
      setLockedDayNumber(dayIndex);
      return;
    }

    const isUnlocked = dayStatuses.get(dayIndex) || false;
    const isToday = dayIndex === currentDayIndex;

    if (!isUnlocked) {
      // Calculate lock reason
      // Calculate target date with month offset support
      const monthOffset = Math.floor(dayIndex / 100);
      const dayOfMonth = dayIndex % 100;
      const targetDate = new Date(year, (month - 1) + monthOffset, dayOfMonth);
      targetDate.setUTCHours(12, 0, 0, 0);

      if (currentDate) {
        const dateUnlocked = currentDate >= targetDate;
        const daysUntilUnlock = !dateUnlocked
          ? Math.ceil((targetDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        if (dayType === 'requireStars') {
          // Day requires a certain number of total stars
          const requiredStars = specialDay?.count;
          if (requiredStars !== undefined) {
            const currentTotalStars = getTotalStars();
            const starsNeeded = requiredStars - currentTotalStars;

            let message = '';
            if (!dateUnlocked && starsNeeded > 0) {
              // Both conditions not met
              const daysText = daysUntilUnlock === 1 ? 'day' : 'days';
              const starsText = starsNeeded === 1 ? 'star' : 'stars';
              message = `This task will be available in ${daysUntilUnlock} ${daysText}, and requires ${starsNeeded} more ${starsText} to unlock!`;
            } else if (!dateUnlocked) {
              // Only date not met
              const daysText = daysUntilUnlock === 1 ? 'day' : 'days';
              message = `This task will unlock in ${daysUntilUnlock} ${daysText}!`;
            } else if (starsNeeded > 0) {
              // Only stars not met
              const starsText = starsNeeded === 1 ? 'star' : 'stars';
              message = `This task requires ${starsNeeded} more ${starsText} to unlock!`;
            }
            setLockedDayMessage(message);
            setLockedDayNumber(dayIndex);
          }
        } else if (dayType === 'requireRow') {
          // Day requires previous tasks to be completed
          const previousDays = [];
          for (let i = 1; i < daysPerRow; i++) {
            const prevDayIndex = dayIndex - i;
            if (availableDays.includes(prevDayIndex)) {
              previousDays.push(prevDayIndex);
            }
          }
          const incompleteCount = previousDays.filter(prevDay => !isDayCompleted(prevDay)).length;

          let message = '';
          if (!dateUnlocked && incompleteCount > 0) {
            // Both conditions not met
            const daysText = daysUntilUnlock === 1 ? 'day' : 'days';
            const tasksText = incompleteCount === 1 ? 'task' : 'tasks';
            message = `This task will unlock in ${daysUntilUnlock} ${daysText}, and requires ${incompleteCount} previous ${tasksText} to be complete!`;
          } else if (!dateUnlocked) {
            // Only date not met
            const daysText = daysUntilUnlock === 1 ? 'day' : 'days';
            message = `This task will unlock in ${daysUntilUnlock} ${daysText}!`;
          } else if (incompleteCount > 0) {
            // Only previous tasks not met
            const tasksText = incompleteCount === 1 ? 'task' : 'tasks';
            message = `This task requires ${incompleteCount} previous ${tasksText} to be complete.`;
          }
          setLockedDayMessage(message);
          setLockedDayNumber(dayIndex);
        } else if (!dateUnlocked) {
          // Day is time-locked (normal days)
          const message = daysUntilUnlock === 1
            ? `This task will unlock in ${daysUntilUnlock} day!`
            : `This task will unlock in ${daysUntilUnlock} days!`;
          setLockedDayMessage(message);
          setLockedDayNumber(dayIndex);
        }
      }
      return;
    }

    sound.select();
    onDayClick(dayIndex, isUnlocked, isToday);
  };

  // Generate array of day numbers that spans across months for available days
  const generateDayNumbers = () => {
    const days = [];

    // Generate dates for each available day based on day number
    // Day numbers encode month: 1-99 = first month, 100-199 = second month, etc.
    for (const dayIndex of availableDays) {
      // Calculate month offset (0 for days 1-99, 1 for days 100-199, etc.)
      const monthOffset = Math.floor(dayIndex / 100);
      // Get actual day of month (1-31)
      const dayOfMonth = dayIndex % 100;

      // Create date in the appropriate month
      const currentDate = new Date(year, (month - 1) + monthOffset, dayOfMonth);

      days.push({
        dayIndex: dayIndex,
        dayOfMonth: currentDate.getDate()
      });
    }

    return days;
  };

  // Don't render calendar until statuses are loaded to prevent flash of incorrect lock states
  if (!statusesLoaded) {
    return null;
  }

  return (
    <>
      {lockedDayMessage && lockedDayNumber !== null && (
        <LockedDayPopup
          message={lockedDayMessage}
          day={lockedDayNumber}
          header={
            specialDays[lockedDayNumber]?.type === 'off'
              ? `${specialDays[lockedDayNumber]?.reason || DEFAULT_OFF_DAY_SYMBOL} Day Off`
              : 'Day Locked'
          }
          isDayOff={specialDays[lockedDayNumber]?.type === 'off'}
          onClose={() => {
            setLockedDayMessage(null);
            setLockedDayNumber(null);
          }}
        />
      )}
      <div
        className="calendar-grid"
        style={{
          gridTemplateColumns: `repeat(${daysPerRow}, 1fr)`
        }}
      >
        {/* Render empty slots if startColumn > 1 and <= daysPerRow */}
        {startColumn > 1 && startColumn <= daysPerRow &&
          Array.from({ length: startColumn - 1 }).map((_, index) => (
            <div key={`empty-${index}`} className="calendar-day-empty" />
          ))
        }
        {generateDayNumbers().map((day, index) => {
        const dayIndex = day.dayIndex;
        const dayOfMonth = day.dayOfMonth;
        const specialDay = specialDays[dayIndex];
        const dayType = specialDay?.type || 'normal';
        const isOff = dayType === 'off';
        const isUnlocked = dayStatuses.get(dayIndex) || false;
        const isCompleted = isDayCompleted(dayIndex);
        const isToday = dayIndex === currentDayIndex;
        const isNewlyUnlocked = newlyUnlockedDays.has(dayIndex);
        const isRequireRow = dayType === 'requireRow';
        const isRequireStars = dayType === 'requireStars';

        // Re-read stars on each render (refreshKey forces re-render when stars change)
        const dayStars = getDayStars(dayIndex);

        return (
            <div
            key={`${dayIndex}-${index}-${refreshKey}`}
            className={`calendar-day ${isOff ? 'off' : (isUnlocked ? 'unlocked' : 'locked')} ${isRequireRow ? 'require-row' : ''} ${isRequireStars ? 'require-stars' : ''} ${isCompleted ? 'completed' : ''} ${isToday ? 'today' : ''} ${isNewlyUnlocked ? 'newly-unlocked' : ''}`}
            onClick={() => handleClick(dayIndex)}
            style={{ cursor: isOff ? 'not-allowed' : (isUnlocked ? 'pointer' : 'not-allowed') }}
            >
            <div className="day-number">
              {isOff ? (specialDay?.reason || DEFAULT_OFF_DAY_SYMBOL) : (dayIndex % 100)}
            </div>
            {!isOff && isCompleted && dayStars > 0 && (
              <div className="stars-icon">
              <StarRating stars={dayStars} animate={false} size="medium" showOnlyFilled={true} />
              </div>
            )}
            {!isOff && (
              <div className="calendar-day-tags">
                {isRequireStars && !isUnlocked && specialDay?.count !== undefined && (
                  <div className="tag-oval">
                    <div className="oval-background"></div>
                    <div className="oval-content">
                      <StarIcon filled={true} size={16} />
                      <span>{specialDay.count}</span>
                    </div>
                  </div>
                )}
                {!isUnlocked && (
                  <div className="tag-circle">
                    <div className="circle-background"></div>
                    <div className="lock-icon" role="img" aria-label="Locked"></div>
                  </div>
                )}
              </div>
            )}
            </div>
        );
      })}
      </div>
    </>
  );
};

export default CalendarGrid;
